import { useState } from 'react';
import { useLocale } from '../../application/i18n/LocaleProvider';
import { selectCareTips } from '../../domain/plants/plantCare';
import { CARE_ICONS, TRAIT_ICONS, WateringCanIcon } from './PlantIcons';
import { MinusCircleIcon, PlusCircleIcon } from '../chrome/BrandIcons';

/**
 * The lede and the care tips — the two pieces of prose on this page.
 *
 * They were one card with two disclosures until the page was put in reading
 * order (SPEC-024 D-10), and the description stopped being a card at all in
 * D-11: what a plant IS is the page speaking, not a panel to open, so it is set
 * as prose on the page's own paper with the traits as labels under it.
 *
 * Care tips is still a card and still opens by default. The toggle is there
 * because a gardener who has read them wants the companions closer to their
 * thumb, not because the page arrives folded up: a detail page whose content
 * starts hidden answers nothing.
 */

function Disclosure({ id, icon: HeadingIcon, title, children }) {
    const { t } = useLocale();
    const [isOpen, setIsOpen] = useState(true);

    return (
        <section className="disclosure" id={id}>
            <div className="disclosure__head">
                <HeadingIcon size={26} />
                <h2 className="disclosure__title">{title}</h2>
                <button
                    type="button"
                    className="disclosure__toggle"
                    onClick={() => setIsOpen((open) => !open)}
                    aria-expanded={isOpen}
                    aria-controls={`${id}-body`}
                    aria-label={t(isOpen ? 'plants.detail.collapse' : 'plants.detail.expand', { section: title })}
                >
                    {isOpen ? <MinusCircleIcon size={24} /> : <PlusCircleIcon size={24} />}
                </button>
            </div>
            {/* Unmounted rather than hidden: a collapsed panel's headings and
                links must leave the tab order, and `display: none` on a wrapper
                is easy to get wrong once a child sets its own display. */}
            {isOpen ? <div className="disclosure__body" id={`${id}-body`}>{children}</div> : null}
        </section>
    );
}

function TraitChips({ traits }) {
    const { t } = useLocale();

    if (!traits?.length) {
        return null;
    }

    return (
        <ul className="trait-chips">
            {traits.map((traitKey) => {
                const TraitIcon = TRAIT_ICONS[traitKey];

                return (
                    <li className="trait-chip" key={traitKey}>
                        {TraitIcon ? <TraitIcon size={20} /> : null}
                        <span>{t(traitKey)}</span>
                    </li>
                );
            })}
        </ul>
    );
}

function CareTips({ plant }) {
    const { t } = useLocale();
    const tips = selectCareTips(plant);

    if (!tips.length) {
        return null;
    }

    return (
        <ul className="care-tips">
            {tips.map((tip) => {
                const TipIcon = CARE_ICONS[tip.key];

                return (
                    <li className="care-tip" key={tip.key}>
                        <TipIcon size={30} />
                        <div>
                            <p className="care-tip__name">{t(tip.labelKey)}</p>
                            <p className="care-tip__body">{tip.text}</p>
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}

/**
 * What the plant IS, in the pack's own words, with its traits labelled under
 * them (SPEC-024 D-11).
 *
 * No card, no heading and no toggle. A card is a container for something the
 * page is showing you; this is the page talking, and it is the first thing a
 * reader meets after the picture — so it is set larger than body copy and stands
 * on the page's paper rather than on a panel of its own. The heading it used to
 * carry said "About Silver Birch" directly under a screen that had just said
 * Silver Birch twice.
 */
export function PlantLede({ plant }) {
    const description = plant.prose?.description;

    // Nothing to say, so nothing drawn. The card this replaced rendered an empty
    // bordered box when a pack had neither.
    if (!description && !plant.traits?.length) {
        return null;
    }

    return (
        <section className="plant-lede">
            {description ? <p className="plant-lede__prose">{description}</p> : null}
            <TraitChips traits={plant.traits} />
        </section>
    );
}

/** How to look after it: the last thing on the page a reader has to read. */
export function PlantCareTipsPanel({ plant, careTipsId }) {
    const { t } = useLocale();
    const tips = selectCareTips(plant);

    if (!tips.length) {
        return null;
    }

    return (
        <div className="plant-panels plant-panels--care">
            <Disclosure id={careTipsId} icon={WateringCanIcon} title={t('plants.detail.careTips')}>
                <CareTips plant={plant} />
            </Disclosure>
        </div>
    );
}
